import { TellescopeModelFromName, useTellescopeMutation, useUser } from "@tellescope/tanstack-query";
import { AISummary, AudioPlayer, Badge, Button, Textarea, TimelineBar } from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { Pencil, Phone, PhoneMissed, Voicemail } from "lucide-react";
import { formatDuration, formatName, parseCallRecordingTranscriptionData } from "@tellescope/utils";
import { useMemo, useRef, useState } from "react";
import { useCallTitle } from "./utils";
import { toast } from "sonner";

export interface ViewCallContentProps {
  call: TellescopeModelFromName<"phone_calls">;
}

export const ViewCallContent = ({ call }: ViewCallContentProps) => {
  const { t } = useTranslation("timeline");
  const callType: "call" | "voicemail" | "missed" = call.isVoicemail
    ? call.recordingURI || call.recordingDurationInSeconds
      ? "voicemail"
      : "missed"
    : "call";
  const answeringUser = useUser(call.userId ?? "");
  const [editingNote, setEditingNote] = useState(false);
  const form = useForm({
    fields: {
      note: {
        type: "textarea",
        initialValue: call.note,
      },
    },
  });
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);

  const phoneCallEditMutation = useTellescopeMutation({
    model: "phone_calls",
    method: "updateOne",

    onSuccess: () => {
      setEditingNote(false);
    },
    onError: () => {
      toast.error(t("Error saving note. Please try again."));
    },
  });

  const title = useCallTitle(call);

  const focusTextarea = () => {
    noteTextareaRef.current?.focus();
  };

  const recordingData = parseCallRecordingTranscriptionData(call.recordingTranscriptionData);
  const transcript = recordingData?.results.transcripts[0]?.transcript;

  const DetailsIcon = callType === "voicemail" ? Voicemail : callType === "missed" ? PhoneMissed : Phone;
  const duration = useMemo(
    () => formatDuration(call.recordingDurationInSeconds ?? 0, "seconds"),
    [call.recordingDurationInSeconds]
  );

  return (
    <>
      {!!call.aiSummary && (
        <AISummary content={call.aiSummary.replace("Call Summary\n", "")} className="mx-4 mt-4 mb-2" />
      )}
      <div className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DetailsIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {callType === "voicemail" ? t("Voicemail Details") : t("Call Details")}
            </h3>
          </div>
          {(!!answeringUser || callType === "voicemail") && (
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
              {callType === "voicemail" ? (
                <>
                  <p className="font-medium text-blue-600">{t("Duration: {{duration}}", { duration })}</p>
                  {duration}
                </>
              ) : call.inbound && answeringUser ? (
                <p className="font-medium text-green-600">
                  {t("Answered by: {{answeringUser}}", { answeringUser: formatName(answeringUser) })}
                </p>
              ) : answeringUser ? (
                <p className="font-medium text-gray-600">
                  {t("Placed by: {{answeringUser}}", { answeringUser: formatName(answeringUser) })}
                </p>
              ) : null}
            </div>
          )}
        </div>
        {callType === "voicemail" && !!call.recordingURI && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 flex flex-col gap-4">
            <AudioPlayer src={call.recordingURI ?? ""} durationOverride={call.recordingDurationInSeconds} />
            {!!call.transcription && (
              <div className="space-y-1">
                <h4 className="text-sm font-medium text-gray-900">{t("Voicemail Transcript:")}</h4>
                <p className="text-xs text-gray-500">{call.transcription}</p>
              </div>
            )}
          </div>
        )}
        {recordingData?.status === "IN_PROGRESS" ? (
          <div className="flex gap-2 items-center justify-center w-full py-2">
            <Spinner size="sm" color="ai" />
            <p className="text-sm text-muted-foreground">{t("Generating transcript...")}</p>
          </div>
        ) : recordingData?.status === "FAILED" ? (
          <p className="text-sm text-destructive py-2">{t("Failed to generate transcript.")}</p>
        ) : transcript ? (
          <AISummary title={t("AI Transcript")} content={`"${transcript}"`} />
        ) : null}
        <div className="border border-gray-200 rounded-lg">
          <div className="border-b p-3 flex justify-between items-center h-[48px]">
            <h3 className="text-sm font-medium text-gray-900">
              {callType === "voicemail" ? t("Voicemail Notes") : t("Call Notes")}
            </h3>

            {!editingNote && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingNote(true);
                  setTimeout(() => {
                    focusTextarea();
                  });
                }}
              >
                <Pencil className="size-4" />
              </Button>
            )}
          </div>
          <form className="max-h-[200px] overflow-y-auto p-3">
            {editingNote ? (
              <Textarea
                {...form.getFieldProps("note")}
                ref={noteTextareaRef}
                onFocus={(e) => {
                  // Set cursor to end of input
                  e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length);
                }}
              />
            ) : (
              <p className={`text-sm ${call.note ? "text-gray-900" : "text-gray-500"}`}>
                {call.note || t("No notes added yet")}
              </p>
            )}
          </form>
        </div>
      </div>
      {!!call.tags?.length && (
        <div className="flex gap-2 py-3 px-4 border-t">
          {call.tags.map((tag, i) => (
            <Badge key={`${tag}-${i}`} variant="outline">
              {tag}
            </Badge>
          ))}
        </div>
      )}
      {editingNote && (
        <TimelineBar
          title={t("Adding note")}
          subtext={title}
          primaryAction={{
            label: t("Save"),
            onClick: () => {
              if (form.values.note !== call.note) {
                phoneCallEditMutation.mutate({
                  id: call.id,
                  note: form.values.note,
                });
              } else {
                setEditingNote(false);
              }
            },
          }}
          secondaryActions={[
            {
              label: t("Cancel"),
              onClick: () => {
                form.setFieldValue("note", call.note ?? "");
                setEditingNote(false);
              },
            },
          ]}
        />
      )}
    </>
  );
};
